import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Loader2, Plus, Route as RouteIcon } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PROGRAMME_STATUS_COLORS, PROGRAMME_STATUS_LABELS, TRAINING_DAYS, WEEK_OPTIONS } from "@/config/trainingCatalog";

export function ProgrammeStatusBadge({ status, testid }) {
  return (
    <Badge data-testid={testid} className={PROGRAMME_STATUS_COLORS[status] || ""}>
      {PROGRAMME_STATUS_LABELS[status] || status}
    </Badge>
  );
}

export default function TrainingTab({ child }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("programmes:manage");
  const [programmes, setProgrammes] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [weeks, setWeeks] = useState("12");
  const [customWeeks, setCustomWeeks] = useState("");
  const [startDate, setStartDate] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [days, setDays] = useState([]);
  const [sessionsPerWeek, setSessionsPerWeek] = useState("3");
  const [notes, setNotes] = useState("");

  const load = () => {
    api.get("/programmes").then((r) => {
      setProgrammes((r.data.items || []).filter((p) => p.child_id === child.id));
    }).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(load, [child.id]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMissing([]);
    try {
      const plannedWeeks = weeks === "custom" ? parseInt(customWeeks, 10) : parseInt(weeks, 10);
      const { data } = await api.post(`/programmes/children/${child.id}`, {
        planned_weeks: plannedWeeks,
        start_date: startDate || null,
        target_completion_date: targetDate || null,
        training_days: days,
        sessions_per_week: parseInt(sessionsPerWeek, 10) || null,
        notes: notes || null,
      });
      toast.success(`Programme ${data.programme_number} created`);
      setOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) setMissing(detail.missing);
      toast.error(typeof detail === "object" ? detail.message : formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (d) => setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));

  return (
    <Card data-testid="training-tab">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2">
          <RouteIcon className="h-5 w-5 text-primary" /> Training programmes
        </CardTitle>
        {canManage && (
          <Button data-testid="programme-create-button" onClick={() => setOpen(true)}
            className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
            <Plus className="h-4 w-4 mr-1" /> New programme
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {programmes === null ? (
          <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : programmes.length === 0 ? (
          <p className="text-sm text-slate-600" data-testid="training-empty">
            No training programme yet. A programme can be created once the Initial Assessment, Route Plan and Risk
            Assessment are all approved.
          </p>
        ) : (
          <div className="space-y-3">
            {programmes.map((p) => (
              <Link key={p.id} to={`/programmes/${p.id}`} data-testid={`programme-card-${p.programme_number}`}
                className="flex flex-wrap items-center gap-3 rounded-md border border-border p-4 hover:bg-muted transition-colors duration-200">
                <div className="min-w-0 flex-1">
                  <p className="font-mono font-semibold text-slate-900">{p.programme_number}</p>
                  <p className="text-xs text-slate-500">
                    {p.planned_weeks} weeks · {p.sessions_completed}/{p.sessions_total} sessions completed
                    {p.target_completion_date ? ` · target ${p.target_completion_date}` : ""}
                  </p>
                </div>
                {p.current_week && <span className="text-xs text-slate-600">Week {p.current_week}</span>}
                {p.behind_schedule && <Badge className="bg-red-50 text-red-700 border-transparent">Behind schedule</Badge>}
                {p.interventions > 0 && <Badge className="bg-amber-100 text-amber-800 border-transparent">{p.interventions} intervention{p.interventions > 1 ? "s" : ""}</Badge>}
                <ProgrammeStatusBadge status={p.status} />
              </Link>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Individual Travel Training Programme</DialogTitle>
            <DialogDescription>
              Requires an approved Initial Assessment, Route Plan and Risk Assessment. Programme length is a plan,
              not an automatic completion rule.
            </DialogDescription>
          </DialogHeader>
          {missing.length > 0 && (
            <div data-testid="programme-missing" role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p className="font-medium">Cannot create yet — missing:</p>
              <ul className="list-disc pl-5">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
            </div>
          )}
          <form onSubmit={create} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Planned duration</Label>
                <Select value={weeks} onValueChange={setWeeks}>
                  <SelectTrigger data-testid="programme-weeks-select" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {WEEK_OPTIONS.map((w) => <SelectItem key={w} value={String(w)}>{w} weeks</SelectItem>)}
                    <SelectItem value="custom">Custom…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {weeks === "custom" && (
                <div className="space-y-1.5">
                  <Label>Custom weeks</Label>
                  <Input data-testid="programme-custom-weeks" type="number" min="1" max="52" value={customWeeks}
                    onChange={(e) => setCustomWeeks(e.target.value)} required />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Start date</Label>
                <Input data-testid="programme-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Target completion</Label>
                <Input data-testid="programme-target-date" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Sessions per week</Label>
                <Input data-testid="programme-sessions-per-week" type="number" min="1" max="10" value={sessionsPerWeek}
                  onChange={(e) => setSessionsPerWeek(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Training days</Label>
              <div className="flex gap-2">
                {TRAINING_DAYS.map((d) => (
                  <button key={d.value} type="button" data-testid={`programme-day-${d.value}`} onClick={() => toggleDay(d.value)}
                    className={`h-11 px-4 rounded-md border text-sm font-medium transition-colors duration-200 ${days.includes(d.value) ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea data-testid="programme-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <Button data-testid="programme-create-submit" type="submit" disabled={saving}
              className="h-11 w-full bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarDays className="h-4 w-4 mr-1" /> Create programme</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
